/* eslint-disable eqeqeq */
import React, { useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { v4 as uuidv4 } from 'uuid';
import { useForm } from 'react-hook-form';
import { FormDialog } from 'components/dialogs/formDialog';
import { DropdownWrapper } from 'components/formInputs/wrappers/dropdownWrapper';
import { TimeWrapper } from 'components/formInputs/wrappers/timeWrapper';
import { renderTime, weekDays } from 'utils/dayTimeHelpers';

export const IikoUsageForm = ({ itemData, isOpen, closeModal, onSave }) => {
  const schema = yup.object().shape({
    usage_id: yup.string().default(itemData?.usage_id),
    day: yup
      .number()
      .nullable(true)
      .transform((_, val) => (val == Number(val) && val !== '' ? Number(val) : null))
      .required('Select a week day')
      .default(itemData?.day || 1),
    from: yup.string().default(itemData ? renderTime(itemData.from_hour, itemData.from_minute) : null),
    until: yup.string().default(itemData ? renderTime(itemData.to_hour, itemData.to_minute) : null),
  });

  const {
    register,
    getValues,
    setError,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: schema.default(),
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (itemData) {
      setValue('usage_id', itemData.usage_id);
      setValue('day', itemData.day);
      setValue('from', renderTime(itemData.from_hour, itemData.from_minute));
      setValue('until', renderTime(itemData.to_hour, itemData.to_minute));
    } else {
      setValue('usage_id', null);
      setValue('day', 1);
      setValue('from', null);
      setValue('until', null);
    }
  }, [itemData]);

  const splitTime = (timeString) => {
    if (!timeString) {
      return { h: null, m: null };
    }

    const splitTimeParts = timeString.split(':');
    const h = parseInt(splitTimeParts[0], 10);
    const m = splitTimeParts.length > 1 ? parseInt(splitTimeParts[1], 10) : 0;

    return { h, m };
  };

  const onFormSubmit = async () => {
    const data = getValues();

    const { from, until } = data;

    const { h: hFrom, m: mFrom } = splitTime(from);
    const { h: hTo, m: mTo } = splitTime(until);

    let anyErrors = false;

    if (Number.isNaN(hFrom) || Number.isNaN(mFrom)) {
      setError('from', { message: 'Invalid time, please provide in HH:mm format' });
      anyErrors = true;
    }

    if (Number.isNaN(hTo) || Number.isNaN(mTo)) {
      setError('until', { message: 'Invalid time, please provide in HH:mm format' });
      anyErrors = true;
    }

    if (anyErrors) {
      return;
    }

    const submitedData = {
      usage_id: data.usage_id || uuidv4(),
      day: parseInt(data.day, 10),
      from_hour: hFrom,
      from_minute: mFrom,
      to_hour: hTo,
      to_minute: mTo,
    };

    onSave(submitedData);
  };

  return (
    <FormDialog
      isOpen={isOpen}
      onClose={closeModal}
      title={itemData ? 'Edit' : 'Add New'}
      buttons={[
        { text: 'Save', onClick: onFormSubmit, color: 'red' },
        { text: 'Cancel', onClick: closeModal, color: 'gray' },
      ]}
    >
      <div className="grid grid-cols-6 gap-6 text-left">
        <DropdownWrapper
          register={register}
          name="day"
          title="Day"
          options={weekDays}
          error={errors?.day}
          responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-6"
        />

        <TimeWrapper
          register={register}
          name="from"
          title="Time from (HH:mm)"
          error={errors?.from}
          responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-3"
          onChange={() => {
            setError('from', null);
          }}
        />

        <TimeWrapper
          register={register}
          name="until"
          title="Time to (HH:mm)"
          error={errors?.until}
          responsiveClassName="col-span-6 sm:col-span-3 md:col-span-6 lg:col-span-3"
          onChange={() => {
            setError('until', null);
          }}
        />
      </div>
    </FormDialog>
  );
};
